import { useMemo, useState } from "react";

import ChevronDown from "@uui/ChevronDown";
import FilterIcon from "@uui/FilterLines";
import { difference, isEmpty } from "lodash/fp";

import ReportSubsection from "@app/analytics/components/reports/ReportSubsection";
import DetailedHighlight from "@app/analytics/MeetingReport/components/DetailedHighlight";
import HighlightFilterMenu, {
  HIGHLIGHT_FILTERS,
} from "@app/analytics/MeetingReport/components/HighlightFilterMenu";
import useMeetingReportHighlights from "@app/analytics/MeetingReport/hooks/useMeetingReportHighlights";
import { FilterCount } from "@app/analytics/MeetingReport/styles";
import type { SessionHighlight } from "@app/common/types";

import Button from "@rui/Button";
import {
  EmptyStateContainer,
  EmptyStateSubtext,
  EmptyStateTitle,
} from "@rui/EmptyStateContainer";
import Stack from "@rui/Stack";

import {
  DeepDiveMenuButton,
  DeepDiveSectionHeader,
  DeepDiveSectionHeading,
} from "./styles";

const DeepDiveHighlights = () => {
  const { highlights } = useMeetingReportHighlights();

  const [excludedTypes, setExcludedTypes] = useState<
    SessionHighlight["type"][]
  >([]);

  const highlightsToShow = useMemo(() => {
    if (isEmpty(excludedTypes)) return highlights;
    return highlights.filter(
      (highlight) => !excludedTypes.includes(highlight.type)
    );
  }, [highlights, excludedTypes]);

  const handleRemoveFilters = () => setExcludedTypes([]);
  const filterCount = HIGHLIGHT_FILTERS.reduce((acc, curr) => {
    const typesExcluded = isEmpty(difference(curr.types, excludedTypes));
    return acc + (typesExcluded ? 1 : 0);
  }, 0);

  const showFilterMenu = !isEmpty(highlights);

  return (
    <ReportSubsection id="highlights">
      <DeepDiveSectionHeader>
        <DeepDiveSectionHeading>Highlights</DeepDiveSectionHeading>
        {showFilterMenu && (
          <HighlightFilterMenu
            excludedTypes={excludedTypes}
            filterCount={filterCount}
            onFilterChange={setExcludedTypes}
          >
            <DeepDiveMenuButton
              startIcon={<FilterIcon />}
              endIcon={<ChevronDown />}
              variant="outlined"
            >
              Filter
              {filterCount > 0 && <FilterCount>{filterCount}</FilterCount>}
            </DeepDiveMenuButton>
          </HighlightFilterMenu>
        )}
      </DeepDiveSectionHeader>
      <Stack>
        {isEmpty(highlightsToShow) ? (
          <EmptyStateContainer sx={{ py: 1.5 }} spacing={1.5}>
            <EmptyStateTitle>Nothing to see here</EmptyStateTitle>
            <EmptyStateSubtext>
              {isEmpty(excludedTypes)
                ? "No highlights generated for this meeting"
                : "Oops, there are no highlights to show for the selected filters. Try removing filters to see more."}
            </EmptyStateSubtext>
            {!isEmpty(excludedTypes) && (
              <Button variant="gradient" onClick={handleRemoveFilters}>
                Remove filters
              </Button>
            )}
          </EmptyStateContainer>
        ) : (
          <Stack spacing={1}>
            {highlightsToShow.map((highlight) => (
              <DetailedHighlight
                key={highlight.id}
                description={highlight.quote ?? highlight.description}
                timestamp={highlight.timestamp}
                type={highlight.type}
                label={highlight.label}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </ReportSubsection>
  );
};

export default DeepDiveHighlights;
